import { useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Container } from '@/components/ui/Container'
import { Button } from '@/components/ui/Button'
import { LocalizedLink } from '@/components/LocalizedLink'

const noMotion = { hidden: {}, visible: {} }

export function CinematicCTA() {
  const { t } = useTranslation()
  const prefersReducedMotion = useReducedMotion()
  const videoRef = useRef<HTMLVideoElement>(null)
  const isInView = useInView(videoRef as React.RefObject<HTMLElement>, { once: false, margin: '0px' })

  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    if (isInView) {
      el.play().catch(() => {})
    } else {
      el.pause()
    }
  }, [isInView])

  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-black">
        <video
          ref={videoRef}
          muted loop playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/videos/controllo-qualita-stampaggio-metalli.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="absolute inset-0 bg-primary-950/80" />

      <Container className="relative z-10 text-center py-24">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
          }}
        >
          <motion.span
            className="block text-4xl md:text-5xl lg:text-6xl font-bold text-white"
            variants={prefersReducedMotion ? noMotion : {
              hidden: { y: 40, opacity: 0, filter: 'blur(8px)' },
              visible: { y: 0, opacity: 1, filter: 'blur(0px)', transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
            }}
          >
            {t('home.cta.titleLine1', 'Hai un progetto?')}
          </motion.span>
          <motion.span
            className="block text-4xl md:text-5xl lg:text-6xl font-bold text-brass mt-2"
            variants={prefersReducedMotion ? noMotion : {
              hidden: { y: 40, opacity: 0, filter: 'blur(8px)' },
              visible: { y: 0, opacity: 1, filter: 'blur(0px)', transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
            }}
          >
            {t('home.cta.titleLine2', 'Parliamone.')}
          </motion.span>
        </motion.div>

        <motion.p
          className="mt-6 text-lg text-white/70 max-w-2xl mx-auto"
          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          {t('home.cta.subtitle', 'Il nostro team è pronto ad assisterti nella realizzazione di componenti su misura.')}
        </motion.p>

        <motion.div
          className="mt-8"
          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
        >
          <Button variant="primary" size="lg" asChild>
            <LocalizedLink to="contacts">
              {t('buttons.contactUs')}
              <ArrowRight className="h-5 w-5" />
            </LocalizedLink>
          </Button>
        </motion.div>
      </Container>
    </section>
  )
}
